import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Store, User, Mail, Phone, Lock, MapPin, Building2, 
  ArrowLeft, Eye, EyeOff, Check 
} from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicRegisterPage = () => {
  const { t } = useI18n();
  const { shopSlug } = useParams();
  const navigate = useNavigate();
  
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    street: '',
    postal_code: '',
    city: '',
    country: 'Deutschland'
  });

  useEffect(() => {
    loadShop();
  }, [shopSlug]);

  const loadShop = async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/shop/${shopSlug}`);
      if (!res.ok) {
        navigate('/');
        return;
      }
      const data = await res.json();
      setShop(data);
    } catch (error) {
      console.error('Failed to load shop', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.password) {
      toast.error(t('fillRequired'));
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error(t('prPasswordMin8'));
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/public/shop/${shopSlug}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Registrierung fehlgeschlagen');
      }

      const data = await res.json();
      
      // Store token in localStorage (shop-specific)
      localStorage.setItem(`b2c_token_${shopSlug}`, data.token);
      localStorage.setItem(`b2c_customer_${shopSlug}`, JSON.stringify(data.customer));
      
      toast.success(t('prRegisterSuccess'));
      navigate(`/shop/${shopSlug}`);
    } catch (error) {
      toast.error(error.message || 'Registrierung fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-black border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Store className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h1 className="text-2xl font-bold mb-2">Shop nicht gefunden</h1>
          <p className="text-gray-500 mb-4">Der gesuchte Shop existiert nicht oder ist nicht aktiv.</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
          >
            {t('toHome')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-register-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(`/shop/${shopSlug}`)}
              className="flex items-center gap-2 text-gray-600 hover:text-black"
            >
              <ArrowLeft className="w-5 h-5" />
              {t('back')}
            </button>
            <div className="flex items-center gap-2">
              {shop.logo_url ? (
                <img 
                  src={`${API_URL}/api/files/${shop.logo_url}`}
                  alt={shop.shop_name}
                  className="h-8 w-8 object-contain"
                />
              ) : (
                <Store className="w-6 h-6 text-gray-600" />
              )}
              <span className="font-bold">{shop.shop_name}</span>
            </div>
            <div className="w-16"></div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm p-8">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2">{t('prCreateAccount')}</h1>
            <p className="text-gray-500">
              Registrieren Sie sich bei {shop.shop_name}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t('firstName')} *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-black"
                    placeholder="Max"
                    required
                    data-testid="first-name-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t('lastName')} *
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="Mustermann"
                  required
                  data-testid="last-name-input"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-2">
                E-Mail *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="max@beispiel.de"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('password')} *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-10 pr-12 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="Mindestens 8 Zeichen"
                  required
                  minLength={8}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Mindestens 8 Zeichen</p>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('phone')}
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="+49 123 456789"
                  data-testid="phone-input"
                />
              </div>
            </div>

            {/* Address Section */}
            <div className="pt-4 border-t">
              <h3 className="text-sm font-medium text-gray-700 mb-4">{t('coDeliveryOptional')}</h3>
              
              <div className="space-y-4">
                <div>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="street"
                      value={formData.street}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-black"
                      placeholder={t('prStreet')}
                      data-testid="street-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <input
                      type="text"
                      name="postal_code"
                      value={formData.postal_code}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-black"
                      placeholder={t('zip')}
                      data-testid="postal-code-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-black"
                        placeholder={t('city')}
                        data-testid="city-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className={`w-full py-4 rounded-xl font-medium text-white transition-all ${
                submitting ? 'bg-gray-400' : 'bg-black hover:bg-gray-800'
              }`}
              data-testid="register-submit-btn"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Wird registriert...
                </span>
              ) : (
                'Konto erstellen'
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Bereits ein Konto?{' '}
              <Link 
                to={`/shop/${shopSlug}/login`}
                className="text-black font-medium hover:underline"
                data-testid="login-link"
              >
                {t('psLogin')}
              </Link>
            </p>
          </div>
        </div>

        {/* Benefits */}
        <div className="mt-8 bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-bold mb-4">Vorteile als Kunde</h3>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-600">{t('prFasterOrders')}</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-600">{t('prOrderHistory')}</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-600">Exklusive Angebote und Rabatte</span>
            </li>
          </ul>
        </div>
      </main>
    </div>
  );
};

export default PublicRegisterPage;
